import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

export function MultiImageGrid({
  previews,
  maxImages,
  aspectRatioValue,
  images,
  onChange,
  errors,
  onDelete,
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: `repeat(${Math.min(3, previews.length + 1, maxImages ?? 3)}, 1fr)`,
        },
        gap: 2,
        width: '100%',
      }}
    >
      {previews.map((url, idx) => (
        <Stack key={idx} position='relative'>
          <Box
            sx={{
              width: '100%',

              aspectRatio: aspectRatioValue,
              overflow: 'hidden',
              backgroundColor: (theme) =>
                errors[idx] && !errors[idx].isValid
                  ? alpha(theme.palette.error.main, 0.1)
                  : alpha(theme.palette.grey[300], 0.1),
              border: (theme) =>
                errors[idx] && !errors[idx].isValid
                  ? `1px solid ${theme.palette.error.main}`
                  : 'none',
              borderRadius: 2,
              '& > img': {
                width: '100%',
                height: '100%',
                objectFit: errors[idx] && !errors[idx].isValid ? 'contain' : 'cover',
              },
            }}
          >
            <img src={url} alt='' />
          </Box>
          <IconButton
            aria-label='Remove image'
            size='small'
            onClick={() => onDelete(idx)}
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              color: 'white',
              backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.5),
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.7),
              },
            }}
          >
            <Icon icon='mdi:delete' width={18} height={18} />
          </IconButton>
        </Stack>
      ))}

      {(typeof maxImages !== 'number' || images.length < maxImages) && (
        <Button
          component='label'
          variant='outlined'
          aria-label='Add image'
          sx={{
            width: '100%',
            height: '100%',
            borderStyle: 'dashed',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            aspectRatio: aspectRatioValue,
            borderRadius: 2,
          }}
        >
          <Icon icon='iconoir:add-media-image' width={48} color='text.secondary' />
          <Typography variant='caption' color='grey.500' mt={1}>
            Add Image
          </Typography>
          <input type='file' accept='image/*' multiple hidden onChange={onChange} />
        </Button>
      )}
    </Box>
  );
}

export function SingleImagePreview({
  images,
  aspectRatioValue,
  onChange,
  previews,
  errors,
  onDelete,
}) {
  if (images.length === 0)
    return (
      <Button
        component='label'
        variant='outlined'
        aria-label='Add image'
        sx={{
          width: '100%',
          maxHeight: '50vh',
          borderStyle: 'dashed',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          aspectRatio: aspectRatioValue,
          borderRadius: 1.5,
        }}
      >
        <Icon icon='iconoir:add-media-image' width={48} color='text.secondary' />
        <Typography variant='caption' color='grey.500' mt={1}>
          Add Image
        </Typography>
        <input type='file' accept='image/*' hidden onChange={onChange} />
      </Button>
    );

  return (
    <Stack position='relative' display='inline-flex'>
      <Box
        sx={{
          overflow: 'hidden',
          border: '1px solid',
          borderColor: errors[0] && !errors[0].isValid ? 'error.main' : 'divider',
          borderRadius: 1.5,
          aspectRatio: aspectRatioValue,
          '& > img': {
            width: '100%',
            height: '100%',
            maxHeight: '50vh',
            objectFit: 'contain',
          },
        }}
      >
        <img src={previews[0]} alt='Uploaded preview' />
      </Box>

      <Stack direction='row' gap={1} sx={{ position: 'absolute', top: 8, right: 8, p: '2px' }}>
        <IconButton
          aria-label='Remove image'
          size='small'
          onClick={() => onDelete(0)}
          sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}
        >
          <Icon icon='mdi:delete' width={18} height={18} />
        </IconButton>
        <IconButton
          aria-label='Replace image'
          size='small'
          component='label'
          sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}
        >
          <Icon icon='mdi:repeat' width={18} height={18} />
          <input type='file' accept='image/*' hidden onChange={onChange} />
        </IconButton>
      </Stack>
    </Stack>
  );
}
